import { Controller } from "@hotwired/stimulus"
import Hls from "hls.js"

/**
 * Live Player Controller
 *
 * Full-featured HLS video player with custom controls.
 * Supports both live streams and VOD playback.
 *
 * Features:
 * - HLS.js for cross-browser support
 * - Native HLS for Safari/iOS
 * - Custom controls (play, volume, progress, fullscreen)
 * - Quality selector (adaptive bitrate)
 * - Live indicator with "go live" button
 * - Error recovery with automatic retry
 *
 * Usage:
 * <div data-controller="live-player"
 *      data-live-player-hls-url-value="https://live.mipadel.net/mipadel/streams/abc.m3u8"
 *      data-live-player-is-live-value="true">
 */
export default class extends Controller {
  static targets = [
    "video", "playBtn", "progress", "progressBar", "progressBuffer",
    "time", "volume", "volumeBar", "volumeBtn",
    "qualityMenu", "qualityBtn", "fullscreenBtn",
    "liveIndicator", "loading", "error", "overlay"
  ]

  static values = {
    hlsUrl: String,
    isLive: { type: Boolean, default: false },
    autoplay: { type: Boolean, default: false },
    poster: { type: String, default: "" }
  }

  connect() {
    this.hls = null
    this.isPlaying = false
    this.isMuted = false
    this.currentQuality = -1 // -1 = Auto
    this.qualities = []
    this.hideControlsTimeout = null

    this._initPlayer()
    this._setupEventListeners()
    this._setupKeyboardShortcuts()
  }

  disconnect() {
    this._cleanup()
  }

  // ==================== Actions ====================

  togglePlay() {
    if (this.videoTarget.paused) {
      this.play()
    } else {
      this.pause()
    }
  }

  play() {
    this.videoTarget.play().catch(err => {
      console.warn("Play prevented:", err)
      this._showPlayOverlay()
    })
  }

  pause() {
    this.videoTarget.pause()
  }

  seek(event) {
    if (!this.hasProgressTarget) return

    const rect = this.progressTarget.getBoundingClientRect()
    const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    const duration = this.videoTarget.duration

    if (isFinite(duration)) {
      this.videoTarget.currentTime = percent * duration
    }
  }

  toggleMute() {
    this.videoTarget.muted = !this.videoTarget.muted
    this.isMuted = this.videoTarget.muted
    this._updateVolumeUI()
  }

  setVolume(event) {
    if (!this.hasVolumeTarget) return

    const rect = this.volumeTarget.getBoundingClientRect()
    const percent = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    this.videoTarget.volume = percent
    this.videoTarget.muted = percent === 0
    this._updateVolumeUI()
  }

  setQuality(event) {
    const level = parseInt(event.currentTarget.dataset.level)
    this.currentQuality = level

    if (this.hls) {
      this.hls.currentLevel = level // -1 = auto
    }

    this._closeQualityMenu()
    this._updateQualityButton()
    this._highlightSelectedQuality()
  }

  toggleQualityMenu(event) {
    event.stopPropagation()
    if (this.hasQualityMenuTarget) {
      this.qualityMenuTarget.classList.toggle("hidden")
    }
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      this.element.requestFullscreen().catch(err => {
        console.warn("Fullscreen error:", err)
      })
    } else {
      document.exitFullscreen()
    }
  }

  goLive() {
    // Jump to live edge
    if (this.hls && this.isLiveValue) {
      const livePosition = this.hls.liveSyncPosition
      if (livePosition) {
        this.videoTarget.currentTime = livePosition
      }
    }
  }

  showControls() {
    this.element.classList.add("controls-visible")
    this._resetHideControlsTimeout()
  }

  hideControls() {
    if (this.isPlaying) {
      this.element.classList.remove("controls-visible")
    }
  }

  // ==================== Private Methods ====================

  _initPlayer() {
    if (!this.hlsUrlValue) {
      console.error("HLS URL not provided")
      this._showError("URL de stream non fournie")
      return
    }

    // Set poster image
    if (this.posterValue) {
      this.videoTarget.poster = this.posterValue
    }

    // Check native HLS support (Safari/iOS)
    if (this.videoTarget.canPlayType("application/vnd.apple.mpegurl")) {
      this._initNativeHLS()
    } else if (Hls.isSupported()) {
      this._initHlsJs()
    } else {
      console.error("HLS not supported")
      this._showError("Votre navigateur ne supporte pas la lecture vidéo HLS")
    }
  }

  _initNativeHLS() {
    this.videoTarget.src = this.hlsUrlValue

    this.videoTarget.addEventListener("loadedmetadata", () => {
      this._hideLoading()
      if (this.autoplayValue) {
        this.play()
      }
    })

    this.videoTarget.addEventListener("error", () => {
      this._handleNativeError()
    })
  }

  _initHlsJs() {
    this.hls = new Hls({
      // Live streaming optimizations
      liveSyncDurationCount: 3,
      liveMaxLatencyDurationCount: 10,
      liveDurationInfinity: this.isLiveValue,
      lowLatencyMode: this.isLiveValue,
      // Buffer settings
      maxBufferLength: this.isLiveValue ? 15 : 30,
      maxMaxBufferLength: this.isLiveValue ? 30 : 60,
      backBufferLength: this.isLiveValue ? 30 : 90,
      // Error recovery
      enableWorker: true,
      fragLoadingMaxRetry: 6,
      fragLoadingRetryDelay: 1000,
      manifestLoadingMaxRetry: 6,
      manifestLoadingRetryDelay: 1000,
      levelLoadingMaxRetry: 6,
      levelLoadingRetryDelay: 1000
    })

    this.hls.loadSource(this.hlsUrlValue)
    this.hls.attachMedia(this.videoTarget)

    this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
      this._onManifestParsed(data)
    })

    this.hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
      this._onLevelSwitched(data)
    })

    this.hls.on(Hls.Events.ERROR, (event, data) => {
      this._onHlsError(data)
    })

    this.hls.on(Hls.Events.FRAG_BUFFERED, () => {
      this._updateBufferProgress()
    })
  }

  _onManifestParsed(data) {
    this._hideLoading()

    // Build quality list
    this.qualities = data.levels.map((level, index) => ({
      index,
      height: level.height,
      bitrate: level.bitrate,
      label: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)}k`
    }))

    this._buildQualityMenu()

    // Show live indicator
    if (this.isLiveValue && this.hasLiveIndicatorTarget) {
      this.liveIndicatorTarget.classList.remove("hidden")
    }

    // Autoplay if requested
    if (this.autoplayValue) {
      this.play()
    }
  }

  _onLevelSwitched(data) {
    this._updateQualityButton()
    this._highlightSelectedQuality()
  }

  _onHlsError(data) {
    console.error("HLS Error:", data.type, data.details)

    if (data.fatal) {
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          console.log("Network error, attempting recovery...")
          this._showLoading()
          setTimeout(() => {
            this.hls.startLoad()
          }, 1000)
          break

        case Hls.ErrorTypes.MEDIA_ERROR:
          console.log("Media error, attempting recovery...")
          this.hls.recoverMediaError()
          break

        default:
          console.error("Fatal HLS error, cannot recover")
          this._showError("Erreur de lecture vidéo. Rechargez la page.")
          break
      }
    }
  }

  _handleNativeError() {
    const error = this.videoTarget.error
    if (error) {
      let message = "Erreur de lecture"
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          message = "Lecture interrompue"
          break
        case MediaError.MEDIA_ERR_NETWORK:
          message = "Erreur réseau"
          break
        case MediaError.MEDIA_ERR_DECODE:
          message = "Erreur de décodage"
          break
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          message = "Format non supporté"
          break
      }
      this._showError(message)
    }
  }

  _setupEventListeners() {
    // Video events
    this.videoTarget.addEventListener("play", () => {
      this.isPlaying = true
      this._updatePlayButton()
      this._resetHideControlsTimeout()
    })

    this.videoTarget.addEventListener("pause", () => {
      this.isPlaying = false
      this._updatePlayButton()
      this.element.classList.add("controls-visible")
    })

    this.videoTarget.addEventListener("timeupdate", () => {
      this._updateProgress()
      this._updateTimeDisplay()
    })

    this.videoTarget.addEventListener("waiting", () => {
      this._showLoading()
    })

    this.videoTarget.addEventListener("playing", () => {
      this._hideLoading()
    })

    this.videoTarget.addEventListener("ended", () => {
      this.isPlaying = false
      this._updatePlayButton()
    })

    this.videoTarget.addEventListener("volumechange", () => {
      this._updateVolumeUI()
    })

    // Fullscreen change
    document.addEventListener("fullscreenchange", () => {
      this._updateFullscreenButton()
    })

    // Click outside quality menu closes it
    document.addEventListener("click", (e) => {
      if (this.hasQualityMenuTarget && !this.qualityMenuTarget.contains(e.target) &&
          (!this.hasQualityBtnTarget || !this.qualityBtnTarget.contains(e.target))) {
        this._closeQualityMenu()
      }
    })

    // Mouse movement for controls visibility
    this.element.addEventListener("mousemove", () => {
      this.showControls()
    })

    this.element.addEventListener("mouseleave", () => {
      if (this.isPlaying) {
        this.hideControls()
      }
    })

    // Touch events for mobile
    this.element.addEventListener("touchstart", () => {
      if (this.element.classList.contains("controls-visible")) {
        this.hideControls()
      } else {
        this.showControls()
      }
    })
  }

  _setupKeyboardShortcuts() {
    this.element.addEventListener("keydown", (e) => {
      switch (e.key) {
        case " ":
        case "k":
          e.preventDefault()
          this.togglePlay()
          break
        case "f":
          e.preventDefault()
          this.toggleFullscreen()
          break
        case "m":
          e.preventDefault()
          this.toggleMute()
          break
        case "ArrowLeft":
          e.preventDefault()
          this.videoTarget.currentTime -= 10
          break
        case "ArrowRight":
          e.preventDefault()
          this.videoTarget.currentTime += 10
          break
        case "ArrowUp":
          e.preventDefault()
          this.videoTarget.volume = Math.min(1, this.videoTarget.volume + 0.1)
          break
        case "ArrowDown":
          e.preventDefault()
          this.videoTarget.volume = Math.max(0, this.videoTarget.volume - 0.1)
          break
      }
    })
  }

  _updatePlayButton() {
    if (!this.hasPlayBtnTarget) return

    if (this.isPlaying) {
      this.playBtnTarget.innerHTML = `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
        </svg>
      `
      this.playBtnTarget.setAttribute("title", "Pause (k)")
    } else {
      this.playBtnTarget.innerHTML = `
        <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z"/>
        </svg>
      `
      this.playBtnTarget.setAttribute("title", "Lecture (k)")
    }
  }

  _updateProgress() {
    if (!this.hasProgressBarTarget) return

    const duration = this.videoTarget.duration
    const currentTime = this.videoTarget.currentTime

    if (isFinite(duration) && duration > 0) {
      const percent = (currentTime / duration) * 100
      this.progressBarTarget.style.width = `${percent}%`
    }
  }

  _updateBufferProgress() {
    if (!this.hasProgressBufferTarget) return

    const duration = this.videoTarget.duration
    const buffered = this.videoTarget.buffered

    if (buffered.length > 0 && isFinite(duration) && duration > 0) {
      const bufferedEnd = buffered.end(buffered.length - 1)
      const percent = (bufferedEnd / duration) * 100
      this.progressBufferTarget.style.width = `${percent}%`
    }
  }

  _updateTimeDisplay() {
    if (!this.hasTimeTarget) return

    const currentTime = this._formatTime(this.videoTarget.currentTime)
    const duration = this._formatTime(this.videoTarget.duration)

    if (this.isLiveValue) {
      this.timeTarget.textContent = currentTime
    } else {
      this.timeTarget.textContent = `${currentTime} / ${duration}`
    }
  }

  _updateVolumeUI() {
    const volume = this.videoTarget.volume
    const muted = this.videoTarget.muted

    if (this.hasVolumeBarTarget) {
      this.volumeBarTarget.style.width = muted ? "0%" : `${volume * 100}%`
    }

    if (this.hasVolumeBtnTarget) {
      if (muted || volume === 0) {
        this.volumeBtnTarget.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.796 8.796 0 0021 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 003.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
          </svg>
        `
      } else if (volume < 0.5) {
        this.volumeBtnTarget.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z"/>
          </svg>
        `
      } else {
        this.volumeBtnTarget.innerHTML = `
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
          </svg>
        `
      }
      this.volumeBtnTarget.setAttribute("title", muted ? "Activer le son (m)" : "Couper le son (m)")
    }
  }

  _buildQualityMenu() {
    if (!this.hasQualityMenuTarget || this.qualities.length === 0) return

    let html = `
      <button class="quality-option w-full px-4 py-2 text-left hover:bg-gray-700 text-sm"
              data-action="click->live-player#setQuality"
              data-level="-1">
        Auto
      </button>
    `

    // Sort by height descending
    const sortedQualities = [...this.qualities].sort((a, b) => b.height - a.height)

    sortedQualities.forEach(q => {
      html += `
        <button class="quality-option w-full px-4 py-2 text-left hover:bg-gray-700 text-sm"
                data-action="click->live-player#setQuality"
                data-level="${q.index}">
          ${q.label}
        </button>
      `
    })

    this.qualityMenuTarget.innerHTML = html
    this._highlightSelectedQuality()
  }

  _updateQualityButton() {
    if (!this.hasQualityBtnTarget) return

    const level = this.hls?.currentLevel ?? -1
    let label = "Auto"

    if (level >= 0 && this.qualities[level]) {
      label = this.qualities[level].label
    }

    this.qualityBtnTarget.innerHTML = `
      <span>${label}</span>
      <svg class="w-4 h-4 ml-1" fill="currentColor" viewBox="0 0 24 24">
        <path d="M7 10l5 5 5-5z"/>
      </svg>
    `
  }

  _highlightSelectedQuality() {
    if (!this.hasQualityMenuTarget) return

    const level = this.hls?.currentLevel ?? -1
    const options = this.qualityMenuTarget.querySelectorAll(".quality-option")

    options.forEach(opt => {
      const optLevel = parseInt(opt.dataset.level)
      if (optLevel === level) {
        opt.classList.add("bg-gray-700", "font-semibold")
      } else {
        opt.classList.remove("bg-gray-700", "font-semibold")
      }
    })
  }

  _closeQualityMenu() {
    if (this.hasQualityMenuTarget) {
      this.qualityMenuTarget.classList.add("hidden")
    }
  }

  _updateFullscreenButton() {
    if (!this.hasFullscreenBtnTarget) return

    const isFullscreen = !!document.fullscreenElement

    if (isFullscreen) {
      this.fullscreenBtnTarget.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"/>
        </svg>
      `
      this.fullscreenBtnTarget.setAttribute("title", "Quitter plein écran (f)")
    } else {
      this.fullscreenBtnTarget.innerHTML = `
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
        </svg>
      `
      this.fullscreenBtnTarget.setAttribute("title", "Plein écran (f)")
    }
  }

  _formatTime(seconds) {
    if (!isFinite(seconds) || isNaN(seconds)) return "00:00"

    const h = Math.floor(seconds / 3600)
    const m = Math.floor((seconds % 3600) / 60)
    const s = Math.floor(seconds % 60)

    if (h > 0) {
      return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    }
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
  }

  _showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("hidden")
    }
  }

  _hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("hidden")
    }
  }

  _showError(message) {
    this._hideLoading()
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.remove("hidden")
    }
  }

  _showPlayOverlay() {
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("hidden")
    } else {
      // Create overlay dynamically
      const overlay = document.createElement("div")
      overlay.className = "absolute inset-0 flex items-center justify-center bg-black/50 cursor-pointer z-10"
      overlay.innerHTML = `
        <svg class="w-20 h-20 text-white opacity-80 hover:opacity-100 transition" fill="currentColor" viewBox="0 0 24 24">
          <path d="M8 5v14l11-7z"/>
        </svg>
      `
      overlay.addEventListener("click", () => {
        this.play()
        overlay.remove()
      })
      this.element.appendChild(overlay)
    }
  }

  _resetHideControlsTimeout() {
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout)
    }
    this.element.classList.add("controls-visible")

    if (this.isPlaying) {
      this.hideControlsTimeout = setTimeout(() => {
        this.element.classList.remove("controls-visible")
      }, 3000)
    }
  }

  _cleanup() {
    if (this.hideControlsTimeout) {
      clearTimeout(this.hideControlsTimeout)
    }

    if (this.hls) {
      this.hls.destroy()
      this.hls = null
    }
  }
}
